import React, { FC, useRef, useState } from 'react';

import { Box } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';

import ActionButton, { ActionLink } from 'components/action-button';
import { CanadaCheckoutLimitMessage } from 'components/canada-order-requirements/canada-checkout-limit-message';
import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import PickupTimeSelector from 'components/pickup-time-selector';
import useEffectOnce from 'hooks/use-effect-once';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import useIsSignUpAfterCart from 'hooks/use-is-sign-up-after-cart';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderStatus, useOrderContext } from 'state/order';
import {
  useCanadaDeliveryLimitState,
  useCanadaOrderScope,
} from 'state/order/hooks/use-canada-order-policy';
import { useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { relativeRoutes } from 'utils/routing';

import DeliveryCancellationPolicy from './delivery-cancellation-policy';
import SignUpToContinueActionLink from './sign-up-to-continue-action-link';
import { CartPriceTooLow, PaymentSectionContainer, SignUpToContinueDisclaimer } from './styled.th';
import { IPaymentProps } from './types';
import { VehicleDescription } from './vehicle-description';

const Payment: FC<IPaymentProps> = ({
  className,
  serverOrder,
  areDeliveryDetailsValid,
  cartDetailsErrorFields,
  pricedAndAvailable,
  orderStatus,
  onPriceOrder,
}) => {
  const { formatMessage } = useIntl();
  const paymentSectionRef = useRef<HTMLDivElement>(null);
  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);
  const checkoutCateringPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_CATERING_MINIMUM);
  const order = useOrderContext();
  const currentCartTotal = order.calculateCartTotal();
  const canadaOrderScope = useCanadaOrderScope();
  const canadaDeliveryLimitState = useCanadaDeliveryLimitState({
    cartPriceLimitExceeded: Boolean(order.cartPriceLimitExceeded),
    cartPriceTooLow: currentCartTotal < checkoutDeliveryPriceMinimum,
  });
  const { getErrorMessages } = useGetPaymentErrors();
  const { isDelivery, isTakeout, isEatIn, isCurbside, isCatering } = useServiceModeContext();
  const isSignUpAfterCart = useIsSignUpAfterCart();
  const [storeIsClosing, setStoreIsClosing] = useState(false);
  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);

  let checkoutPriceMinimum = 0;
  if (isDelivery) {
    checkoutPriceMinimum = checkoutDeliveryPriceMinimum;
  } else if (isCatering) {
    checkoutPriceMinimum = checkoutCateringPriceMinimum;
  }

  const cartSubTotal = isSignUpAfterCart ? currentCartTotal : serverOrder?.cart?.subTotalCents;
  const cartPriceTooLow = cartSubTotal < checkoutPriceMinimum;
  const { formatCurrencyForLocale } = useUIContext();
  const initialValue = LocalStorage.getItem(StorageKeys.VEHICLE_DESCRIPTION);
  const [
    { value: vehicleDescription, hasError: vehicleDescriptionHasError },
    setVehicleDescription,
  ] = useState({
    value: initialValue,
    hasError: false,
  });

  const focusElementWithError = (name: string) => {
    // TODO: Remove queryselector and replace with ref's
    const element: HTMLElement | null | undefined = paymentSectionRef.current?.querySelector(
      `[name=${name}]`
    );
    if (element) {
      element.focus();
    }
  };

  const handleErrorContinueClick = () => {
    setIsCheckoutErrorsModalOpen(true);
  };
  const handleContinueClick = () => {
    if (isCatering && onPriceOrder) {
      onPriceOrder();
    }
    if (isCurbside) {
      if (!vehicleDescriptionHasError && vehicleDescription) {
        LocalStorage.setItem(StorageKeys.VEHICLE_DESCRIPTION, vehicleDescription);
      } else {
        return focusElementWithError('vehicleDescription');
      }
    }
  };

  const isOrderPriceLoading = !serverOrder || orderStatus === OrderStatus.PRICE_REQUESTED;
  const isContinueButtonDisabled =
    isOrderPriceLoading || !!order?.isPricingOrder || canadaDeliveryLimitState.kind !== 'clear';

  useEffectOnce(() => {
    if (isCurbside || isDelivery) {
      order.setFireOrderIn(3600);
    }
  });

  const paymentErrorMessages = getErrorMessages({
    cartPriceTooLow: isDelivery && cartPriceTooLow,
    cartCateringPriceTooLow: isCatering && cartPriceTooLow,
    areDeliveryDetailsWithError: !areDeliveryDetailsValid,
    cartDetailsErrorFields,
    curbsideOrderError: isCurbside && (vehicleDescriptionHasError || !vehicleDescription),
    pricedAndAvailableError: !pricedAndAvailable,
    storeIsClosing,
  });

  const hasErrors = !!paymentErrorMessages.length;
  const hasCanadaDeliveryLimitError = canadaDeliveryLimitState.kind !== 'clear';

  const ContinueButton = () => {
    if (hasCanadaDeliveryLimitError) {
      return (
        <ActionButton fullWidth isLoading={isOrderPriceLoading} disabled>
          {formatMessage({ id: 'continue' })}
        </ActionButton>
      );
    }

    return hasErrors ? (
      <ActionButton
        onClick={handleErrorContinueClick}
        fullWidth
        isLoading={isOrderPriceLoading}
        disabled={isContinueButtonDisabled}
      >
        {formatMessage({ id: 'continue' })}
      </ActionButton>
    ) : (
      <ActionLink
        onClick={handleContinueClick}
        to={relativeRoutes.orderPayment}
        fullWidth
        isLoading={isOrderPriceLoading}
        disabled={isContinueButtonDisabled}
      >
        {formatMessage({ id: 'continue' })}
      </ActionLink>
    );
  };

  return (
    <PaymentSectionContainer
      ref={paymentSectionRef}
      className={className}
      isCatering={isCatering}
      data-testid="cart-payment"
    >
      {isCheckoutErrorsModalOpen && (
        <CheckoutErrorsModal
          checkoutErrors={paymentErrorMessages}
          closeModal={() => {
            setIsCheckoutErrorsModalOpen(false);
          }}
        />
      )}
      {isCurbside && !isSignUpAfterCart && (
        <VehicleDescription
          onChange={setVehicleDescription}
          value={vehicleDescription}
          hasError={vehicleDescriptionHasError}
        />
      )}
      {(isTakeout || isEatIn) && !isSignUpAfterCart && (
        <PickupTimeSelector
          onChange={order.setFireOrderIn}
          value={order.fireOrderIn}
          setStoreIsClosing={setStoreIsClosing}
        />
      )}
      {cartPriceTooLow && canadaOrderScope !== 'canada-delivery' && (
        <CartPriceTooLow>
          {formatMessage(
            {
              id: 'deliveryMinumumNotReached',
            },
            {
              min: formatCurrencyForLocale(checkoutDeliveryPriceMinimum),
            }
          )}
          <br />
          {formatMessage({ id: 'addSomethingExtra' })}
        </CartPriceTooLow>
      )}
      <CanadaCheckoutLimitMessage state={canadaDeliveryLimitState} />
      <Box margin="1rem 0 0">
        {isSignUpAfterCart ? (
          <div>
            <SignUpToContinueDisclaimer>
              {formatMessage({ id: 'signUpToContinueDisclaimer' })}
            </SignUpToContinueDisclaimer>
            <SignUpToContinueActionLink disabled={hasCanadaDeliveryLimitError} />
          </div>
        ) : (
          <ContinueButton />
        )}
      </Box>
      {isDelivery && <DeliveryCancellationPolicy />}
    </PaymentSectionContainer>
  );
};

export default Payment;
